import { useState, useEffect } from "react";

interface WindowSize {
  width: number | undefined;
  height: number | undefined;
  /** Viewport width &lt; 768px */
  isMobile: boolean | undefined;
  /** Phones + tablets: dual fixed top/bottom bars (&lt; 1024px) */
  isCompactNav: boolean | undefined;
  scrollX?: number;
  scrollY?: number;
}

interface UseWindowSizeOptions {
  isScroll?: boolean;
}

const useWindowSize = ({ isScroll = false }: UseWindowSizeOptions = {}) => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
    isMobile: undefined,
    isCompactNav: undefined,
    ...(isScroll && { scrollX: undefined, scrollY: undefined }),
  });

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const newSize: WindowSize = {
        width: w,
        height: window.innerHeight,
        isMobile: w < 768,
        isCompactNav: w < 1024,
        ...(isScroll && {
          scrollX: window.pageXOffset || document.documentElement.scrollLeft,
          scrollY: window.pageYOffset || document.documentElement.scrollTop,
        }),
      };
      setWindowSize(newSize);
    };

    const handleScroll = () => {
      if (isScroll) {
        const w = window.innerWidth;
        const newSize: WindowSize = {
          width: w,
          height: window.innerHeight,
          isMobile: w < 768,
          isCompactNav: w < 1024,
          scrollX: window.pageXOffset || document.documentElement.scrollLeft,
          scrollY: window.pageYOffset || document.documentElement.scrollTop,
        };
        setWindowSize(newSize);
      }
    };

    window.addEventListener("resize", handleResize);
    if (isScroll) {
      window.addEventListener("scroll", handleScroll);
    }

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (isScroll) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isScroll]);

  return windowSize;
};

export default useWindowSize;
